import { FormControl, FormGroup } from '@angular/forms';
import { CUSTOM_NAME_ERROR, isCssCustomNameValid } from '@finxone-platform/shared/sys-config-types';
import { renameFormGroupControl, reorderFormGroupControls } from './global-theme-form-group-reorder.utils';

/** Builds an empty custom-group FormGroup, ready to have colour controls added to it. */
export function createEmptyCustomGroup(): FormGroup {
  return new FormGroup({});
}

/** Builds a custom-group FormGroup pre-populated with one FormControl per colour. */
export function buildCustomGroupFormGroup(colours: Record<string, string> = {}): FormGroup {
  const controls: Record<string, FormControl<string>> = {};
  for (const [name, hex] of Object.entries(colours)) {
    controls[name] = new FormControl(hex ?? '') as FormControl<string>;
  }
  return new FormGroup(controls);
}

/** Adds a new custom group as a sibling control on the palette FormGroup. */
export function addCustomGroup(paletteFormGroup: FormGroup, groupName: string): void {
  paletteFormGroup.addControl(groupName, createEmptyCustomGroup());
}

/** Renames a custom group in place, keeping its position among its siblings. */
export function renameCustomGroup(paletteFormGroup: FormGroup, oldName: string, newName: string): void {
  renameFormGroupControl(paletteFormGroup, oldName, newName);
}

/** Removes a custom group entirely from the palette FormGroup. */
export function removeCustomGroup(paletteFormGroup: FormGroup, groupName: string): void {
  paletteFormGroup.removeControl(groupName);
}

/** Adds a new colour control to a custom group's FormGroup. */
export function addColourToGroup(groupFormGroup: FormGroup, colourName: string, hexValue: string): void {
  groupFormGroup.addControl(colourName, new FormControl(hexValue));
}

/** Renames a colour within a custom group in place, keeping its position among its siblings. */
export function renameColourInGroup(groupFormGroup: FormGroup, oldName: string, newName: string): void {
  renameFormGroupControl(groupFormGroup, oldName, newName);
}

export function colourNameError(newName: string, oldName: string, colourKeys: string[]): string | null {
  if (!newName) return 'Please enter a valid name';
  if (!isCssCustomNameValid(newName)) return CUSTOM_NAME_ERROR;
  if (newName !== oldName && colourKeys.includes(newName)) {
    return `A colour named "${newName}" already exists in this group`;
  }
  return null;
}

/** Removes a colour control from a custom group. */
export function removeColourFromGroup(groupFormGroup: FormGroup, colourName: string): void {
  groupFormGroup.removeControl(colourName);
}

/** Reorders a group's colour controls, driven by PrimeNG's row-reorder drag/drop indices. */
export function reorderColourInGroup(groupFormGroup: FormGroup, fromIndex: number, toIndex: number): void {
  reorderFormGroupControls(groupFormGroup, fromIndex, toIndex);
}
